import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ClipboardCheck, FileClock, Save, ShieldCheck } from "lucide-react";
import { API_URL } from "../config";
import { apiRequest, formatApiError } from "../apiRequest";
import KpiPageHeader from "../components/KpiPageHeader";

function istToday() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit", day: "2-digit"
  }).formatToParts(new Date());
  const values = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
  return `${values.year}-${values.month}-${values.day}`;
}

async function kpiRequest(path, method = "GET", body) {
  const { data } = await apiRequest(`${API_URL}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
      ...(body === undefined ? {} : { "Content-Type": "application/json" })
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) })
  }, { retries: 0 });
  return data;
}

function parseStoredValue(value) {
  if (value == null || value === "") return null;
  if (typeof value !== "string") return value;
  try { return JSON.parse(value); } catch { return value; }
}

function fieldNames(item) {
  const rule = parseStoredValue(item.scoring_rule_json ?? item.scoring_rule) || {};
  if (["percentage_ratio", "target_achievement", "compliance"].includes(rule.type)) {
    return [rule.actualField || "actual", rule.targetField || "target"];
  }
  if (rule.type === "quality_score") {
    return item.maximum_score != null || rule.maximum != null
      ? [rule.scoreField || "score"]
      : [rule.scoreField || "score", rule.maximumField || "maximum"];
  }
  return [];
}

function controlValue(item, input, updateItemValue) {
  const rule = parseStoredValue(item.scoring_rule_json ?? item.scoring_rule) || {};
  if (rule.type === "binary" || (rule.type === "compliance" && rule.mapping)) {
    return <select aria-label={`${item.display_name} value`} value={input.value === true ? "true" : input.value === false ? "false" : ""}
      onChange={(event) => updateItemValue(item.metric_key, event.target.value === "" ? null : event.target.value === "true")}>
      <option value="">Choose</option><option value="true">Yes</option><option value="false">No</option>
    </select>;
  }
  if (rule.type === "percentage_ratio" && rule.allowSuppliedPercentage) {
    const fields = [rule.actualField || "actual", rule.targetField || "target"];
    const pair = input.value && typeof input.value === "object" && !Array.isArray(input.value);
    return <div className="kpi-daily-percentage-source">
      <select aria-label={`${item.display_name} input type`} value={pair ? "pair" : "percentage"}
        onChange={(event) => updateItemValue(item.metric_key, event.target.value === "pair" ? { [fields[0]]: null, [fields[1]]: null } : null)}>
        <option value="percentage">Supplied percentage</option><option value="pair">Actual and target</option>
      </select>
      {pair ? <div className="kpi-daily-composite">{fields.map((field) => <label className="kpi-daily-number" key={field}><span>{field}</span><input type="number" min="0" step="any" value={input.value[field] ?? ""} aria-label={`${item.display_name} ${field}`} onChange={(event) => updateItemValue(item.metric_key, { ...input.value, [field]: numberOrEmpty(event.target.value) })} /></label>)}</div> : <label className="kpi-daily-number"><span>Percentage</span><input type="number" min="0" step="any" value={input.value ?? ""} aria-label={`${item.display_name} percentage`} onChange={(event) => updateItemValue(item.metric_key, numberOrEmpty(event.target.value))} /></label>}
    </div>;
  }
  if (rule.type === "count_threshold") {
    return <label className="kpi-daily-number"><span>Count</span>
      <input type="number" min="0" step="any" value={input.value ?? ""} aria-label={`${item.display_name} source value`}
        onChange={(event) => updateItemValue(item.metric_key, numberOrEmpty(event.target.value))} /></label>;
  }
  if (rule.type === "multi_rater") {
    const reviewers = input.value?.reviewers || {};
    return <div className="kpi-daily-composite">{(rule.expectedReviewers || []).map((name) => (
      <label className="kpi-daily-number" key={name}><span>{name}</span>
        <input type="number" min="0" max={rule.maximumRating} step="any" value={reviewers[name] ?? ""} aria-label={`${item.display_name} ${name} rating`}
          onChange={(event) => updateItemValue(item.metric_key, { reviewers: { ...reviewers, [name]: numberOrEmpty(event.target.value) } })} />
      </label>
    ))}</div>;
  }
  if (fieldNames(item).length) {
    const value = input.value && typeof input.value === "object" ? input.value : {};
    return <div className="kpi-daily-composite">{fieldNames(item).map((field) => (
      <label className="kpi-daily-number" key={field}><span>{field}</span>
        <input type="number" min="0" step="any" value={value[field] ?? ""} aria-label={`${item.display_name} ${field}`}
          onChange={(event) => updateItemValue(item.metric_key, { ...value, [field]: numberOrEmpty(event.target.value) })} />
      </label>
    ))}</div>;
  }
  return <input className="kpi-daily-unsupported" value="Unsupported configuration" disabled aria-label={`${item.display_name} input unavailable`} />;
}

function numberOrEmpty(value) {
  if (value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function statusLabel(value) {
  return ({ recorded: "Recorded", not_recorded: "Not recorded", not_applicable: "Not applicable" })[value] || "Not saved";
}

function formatValue(value) {
  const parsed = parseStoredValue(value);
  if (parsed == null) return "—";
  if (typeof parsed === "boolean") return parsed ? "Yes" : "No";
  if (typeof parsed === "number" || typeof parsed === "string") return String(parsed);
  if (parsed.reviewers && typeof parsed.reviewers === "object") {
    return Object.entries(parsed.reviewers).map(([name, rating]) => `${name}: ${rating ?? "—"}`).join(" · ");
  }
  return Object.entries(parsed).map(([name, entry]) => `${name}: ${entry ?? "—"}`).join(" · ");
}

export default function KpiDailyPage({ mode: initialMode = "entry", onBack, backLabel = "KPI", onHome, onNavigate }) {
  const [mode, setMode] = useState(initialMode);
  const [businessDate, setBusinessDate] = useState(istToday);
  const [team, setTeam] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [teams, setTeams] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [context, setContext] = useState(null);
  const [values, setValues] = useState({});
  const [correctionReason, setCorrectionReason] = useState("");
  const [metrics, setMetrics] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [filter, setFilter] = useState({ date: "", from: "", to: "", team: "", employeeId: "", metricKey: "", valueStatus: "" });
  const [history, setHistory] = useState({ rows: [], page: 1, total: 0, totalPages: 0 });
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const correctionTarget = useRef(null);

  const isRestricted = useMemo(() => Boolean(localStorage.getItem("user") && (() => {
    try { const user = JSON.parse(localStorage.getItem("user")); return user?.role === "agent" && String(user?.dsrRole || "").toLowerCase() === "tl"; }
    catch { return false; }
  })()), []);

  const loadInitial = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [teamData, metricData] = await Promise.all([
        kpiRequest(`/kpi-daily/teams?date=${encodeURIComponent(businessDate)}`),
        kpiRequest("/kpi-daily/metrics")
      ]);
      setTeams(teamData.teams || []);
      setMetrics(metricData.metrics || []);
      if (isRestricted) setTeam(teamData.teams?.[0] || "");
    } catch (err) { setError(formatApiError(err, "Unable to load KPI entry options")); }
    finally { setLoading(false); }
  }, [businessDate, isRestricted]);

  useEffect(() => { loadInitial(); }, [loadInitial]);

  useEffect(() => {
    if (!team || mode !== "entry") { setEmployees([]); setEmployeeId(""); setContext(null); return; }
    let active = true;
    setLoading(true);
    if (!correctionTarget.current) setEmployeeId("");
    setContext(null); setValues({});
    kpiRequest(`/kpi-daily/employees?date=${encodeURIComponent(businessDate)}&team=${encodeURIComponent(team)}`)
      .then((data) => {
        if (!active) return;
        setEmployees(data.employees || []);
        if (correctionTarget.current?.user_id) {
          setEmployeeId(correctionTarget.current.user_id);
          correctionTarget.current = null;
        }
      })
      .catch((err) => { if (active) setError(formatApiError(err, "Unable to load employees")); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [businessDate, team, mode]);

  useEffect(() => {
    if (!employeeId || mode !== "entry") { setContext(null); return; }
    let active = true;
    setLoading(true); setError(""); setNotice("");
    kpiRequest(`/kpi-daily/context?date=${encodeURIComponent(businessDate)}&employeeId=${encodeURIComponent(employeeId)}`)
      .then((data) => {
        if (!active) return;
        setContext(data);
        const next = {};
        for (const entry of data.entries || []) {
          next[entry.metric_key] = { valueStatus: entry.value_status, value: entry.value };
        }
        setValues(next);
        setCorrectionReason("");
      })
      .catch((err) => { if (active) setError(formatApiError(err, "Unable to resolve KPI configuration")); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [businessDate, employeeId, mode]);

  useEffect(() => {
    if (mode !== "history") return;
    let active = true;
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filter)) if (value) params.set(key, value);
    params.set("page", String(history.page));
    params.set("limit", "50");
    setLoading(true); setError("");
    kpiRequest(`/kpi-daily/history?${params}`)
      .then((data) => { if (active) setHistory(data); })
      .catch((err) => { if (active) setError(formatApiError(err, "Unable to load daily KPI history")); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [filter, history.page, mode]);

  useEffect(() => {
    if (mode !== "history") return;
    kpiRequest(`/kpi-daily/teams?date=${encodeURIComponent(businessDate)}`).then((data) => {
      setTeams(data.teams || []);
      if (isRestricted) setFilter((current) => ({ ...current, team: data.teams?.[0] || "" }));
    }).catch(() => {});
  }, [mode, businessDate, isRestricted]);

  useEffect(() => {
    if (mode !== "history") { setEmployees([]); return; }
    const teamParam = filter.team ? `&team=${encodeURIComponent(filter.team)}` : "";
    kpiRequest(`/kpi-daily/employees?date=${encodeURIComponent(businessDate)}${teamParam}`)
      .then((data) => setEmployees(data.employees || []))
      .catch(() => setEmployees([]));
  }, [mode, businessDate, filter.team]);

  const applicableItems = context?.configuration?.items || [];
  const savedRecords = context?.entries || [];
  const hasChangesToExisting = applicableItems.some((item) => {
    const saved = savedRecords.find((entry) => entry.metric_key === item.metric_key);
    const input = values[item.metric_key];
    return saved && input?.valueStatus && (saved.value_status !== input.valueStatus || JSON.stringify(saved.value) !== JSON.stringify(input.value));
  });

  function updateItemValue(metricKey, value) {
    setValues((current) => ({ ...current, [metricKey]: { ...current[metricKey], value } }));
  }

  const save = async (event) => {
    event.preventDefault();
    const entries = applicableItems.filter((item) => values[item.metric_key]?.valueStatus).map((item) => ({
      metricKey: item.metric_key,
      valueStatus: values[item.metric_key].valueStatus,
      ...(values[item.metric_key].valueStatus === "recorded" ? { value: values[item.metric_key].value } : {})
    }));
    if (!entries.length) { setError("Choose a value status for at least one applicable KPI."); return; }
    if (hasChangesToExisting && !correctionReason.trim()) { setError("Enter a reason for the historical correction."); return; }
    setSaving(true); setError(""); setNotice("");
    try {
      const result = await kpiRequest("/kpi-daily/entries", "POST", {
        employeeId, businessDate, entries, ...(correctionReason.trim() ? { correctionReason: correctionReason.trim() } : {})
      });
      setNotice(`${result.saved} KPI value${result.saved === 1 ? "" : "s"} saved${result.unchanged ? `; ${result.unchanged} unchanged` : ""}.`);
      const refreshed = await kpiRequest(`/kpi-daily/context?date=${encodeURIComponent(businessDate)}&employeeId=${encodeURIComponent(employeeId)}`);
      setContext(refreshed);
      const next = {};
      for (const entry of refreshed.entries || []) next[entry.metric_key] = { valueStatus: entry.value_status, value: entry.value };
      setValues(next); setCorrectionReason("");
    } catch (err) { setError(formatApiError(err, "KPI values could not be saved")); }
    finally { setSaving(false); }
  };

  async function openAudit(entry) {
    setAuditLoading(true); setSelectedAudit({ entry, events: [] });
    try { setSelectedAudit(await kpiRequest(`/kpi-daily/entries/${encodeURIComponent(entry.id)}/audit`)); }
    catch (err) { setError(formatApiError(err, "Unable to load record audit history")); }
    finally { setAuditLoading(false); }
  }

  function beginCorrection(entry) {
    correctionTarget.current = { user_id: entry.user_id, business_date: entry.business_date };
    setBusinessDate(entry.business_date);
    setTeam(entry.current_team || entry.team_snapshot);
    setEmployeeId("");
    setMode("entry");
    setNotice("Historical correction selected. The original configuration and team/designation snapshots will be retained.");
  }

  return (
    <main className="kpi-module-page kpi-daily-page">
      <KpiPageHeader title="KPI Daily Data" subtitle={mode === "entry" ? "Daily entry & corrections" : "Daily history & audit records"} icon={ClipboardCheck} onBack={onHome || onBack} backLabel={onHome ? "Home" : backLabel} activeView={`daily-${mode}`} onNavigate={onNavigate}>
        {!onNavigate && <nav className="kpi-daily-tabs" aria-label="KPI daily views">
          <button type="button" className={mode === "entry" ? "selected" : ""} onClick={() => { setMode("entry"); setError(""); }}>Daily Entry</button>
          <button type="button" className={mode === "history" ? "selected" : ""} onClick={() => { setMode("history"); setHistory((current) => ({ ...current, page: 1 })); setError(""); }}>Daily History</button>
        </nav>}
      </KpiPageHeader>
      {error && <div className="kpi-config-message error" role="alert">{error}</div>}
      {notice && <div className="kpi-config-message success" role="status">{notice}</div>}
      {loading && <p className="kpi-config-loading" role="status">Loading KPI data…</p>}

      {mode === "entry" ? <section className="kpi-daily-entry">
        <div className="kpi-daily-filters">
          <label className="kpi-config-field"><span>Business date</span><input type="date" value={businessDate} onChange={(event) => { setBusinessDate(event.target.value); setContext(null); setEmployeeId(""); }} /></label>
          <label className="kpi-config-field"><span>Team</span><select value={team} disabled={isRestricted} onChange={(event) => setTeam(event.target.value)}><option value="">Choose team</option>{teams.map((item) => <option key={item}>{item}</option>)}</select></label>
          <label className="kpi-config-field"><span>Employee</span><select value={employeeId} disabled={!team} onChange={(event) => setEmployeeId(event.target.value)}><option value="">Choose employee</option>{employees.map((employee) => <option key={employee.id} value={employee.id}>{employee.name} · {employee.designation}</option>)}</select></label>
        </div>
        {context && <>
          <div className="kpi-daily-employee-summary">
            <div><span>Employee</span><strong>{context.employee.name}</strong><small>{context.employee.designation} · {context.employee.team || "No team"}</small></div>
            <div><span>Business date</span><strong>{businessDate}</strong></div>
            <div><span>Published configuration</span><strong>{context.configuration ? `${context.configuration.designation} v${context.configuration.version_number}` : "Unavailable"}</strong>
              {context.configuration && <small>Effective {context.configuration.effective_from}{context.configuration.effective_to ? ` to ${context.configuration.effective_to}` : " onward"}</small>}
            </div>
          </div>
          {!context.configuration ? <div className="kpi-daily-empty" role="status">No published KPI configuration exists for {context.employee.designation} on {businessDate}. No draft or demo configuration was used.</div> : (
            <form onSubmit={save}>
              {context.configuration.employeeSnapshotChanged && <div className="kpi-daily-empty" role="status">This employee’s current team or designation differs from this saved historical record. Only existing values can be corrected; the original configuration and snapshots stay pinned.</div>}
              <div className="kpi-daily-items">
                <div className="kpi-daily-items-head"><span>KPI</span><span>Weight</span><span>Source value</span><span>Value status</span><span>Source</span></div>
                {applicableItems.map((item) => {
                  const input = values[item.metric_key] || { valueStatus: "", value: null };
                  const existing = savedRecords.find((entry) => entry.metric_key === item.metric_key);
                  const historicalNewValueBlocked = context.configuration.employeeSnapshotChanged && !existing;
                  return <article className={`kpi-daily-item${item.is_active ? "" : " inactive"}`} key={item.metric_key}>
                    <div className="kpi-daily-metric"><strong>{item.display_name}</strong>{item.description && <small>{item.description}</small>}
                      {item.target_config_json && <small>Context: {Object.entries(parseStoredValue(item.target_config_json) || {}).map(([key, value]) => `${key} ${value}`).join(" · ")}</small>}
                      {!item.is_active && <small className="kpi-daily-muted">Inactive in this published configuration</small>}
                    </div>
                    <strong className="kpi-daily-weight">{item.weight_percent ?? "—"}%</strong>
                    <div className="kpi-daily-value-control">{input.valueStatus === "recorded" && item.is_active && !historicalNewValueBlocked ? controlValue(item, input, updateItemValue) : <span className="kpi-daily-placeholder">{historicalNewValueBlocked ? "Historical source value is not present" : input.valueStatus === "not_recorded" ? "No source value" : input.valueStatus === "not_applicable" ? "Not applicable" : "Choose status"}</span>}</div>
                    <label className="kpi-config-field"><span className="kpi-daily-mobile-label">Value status</span><select aria-label={`${item.display_name} status`} disabled={!item.is_active || historicalNewValueBlocked} value={input.valueStatus || ""} onChange={(event) => setValues((current) => ({ ...current, [item.metric_key]: { ...current[item.metric_key], valueStatus: event.target.value, value: event.target.value === "recorded" ? (current[item.metric_key]?.value ?? null) : null } }))}>
                      <option value="">Choose</option><option value="recorded">Recorded</option><option value="not_recorded">Not recorded</option><option value="not_applicable">Not applicable</option>
                    </select></label>
                    <span className="kpi-daily-source">{existing ? <>{statusLabel(existing.value_status)}<small>Manual · v{existing.record_version}</small><small>Previous: {formatValue(existing.value)}</small></> : "Manual"}</span>
                  </article>;
                })}
              </div>
              {hasChangesToExisting && <label className="kpi-config-field kpi-daily-reason"><span>Correction reason · required for saved-value changes</span><input value={correctionReason} maxLength={500} onChange={(event) => setCorrectionReason(event.target.value)} placeholder="Explain why this historical value is being corrected" /></label>}
              <div className="kpi-daily-savebar"><span>Values are stored as source data; no performance score is saved.</span><button className="kpi-primary-button" disabled={saving} type="submit"><Save size={16} aria-hidden="true" />{saving ? "Saving…" : "Save KPI Data"}</button></div>
            </form>
          )}
        </>}
      </section> : <section className="kpi-daily-history">
        <div className="kpi-daily-filters kpi-daily-history-filters">
          <label className="kpi-config-field"><span>Date</span><input type="date" value={filter.date} onChange={(event) => { setFilter({ ...filter, date: event.target.value, from: "", to: "" }); setHistory((current) => ({ ...current, page: 1 })); }} /></label>
          <label className="kpi-config-field"><span>From</span><input type="date" value={filter.from} onChange={(event) => { setFilter({ ...filter, date: "", from: event.target.value }); setHistory((current) => ({ ...current, page: 1 })); }} /></label>
          <label className="kpi-config-field"><span>To</span><input type="date" value={filter.to} onChange={(event) => { setFilter({ ...filter, date: "", to: event.target.value }); setHistory((current) => ({ ...current, page: 1 })); }} /></label>
          <label className="kpi-config-field"><span>Team</span><select value={filter.team} disabled={isRestricted} onChange={(event) => { setFilter({ ...filter, team: event.target.value, employeeId: "" }); setHistory((current) => ({ ...current, page: 1 })); }}><option value="">All teams</option>{teams.map((item) => <option key={item}>{item}</option>)}</select></label>
          <label className="kpi-config-field"><span>Employee</span><select value={filter.employeeId} onChange={(event) => { setFilter({ ...filter, employeeId: event.target.value }); setHistory((current) => ({ ...current, page: 1 })); }}><option value="">All employees</option>{employees.map((employee) => <option key={employee.id} value={employee.id}>{employee.name}</option>)}</select></label>
          <label className="kpi-config-field"><span>KPI</span><select value={filter.metricKey} onChange={(event) => { setFilter({ ...filter, metricKey: event.target.value }); setHistory((current) => ({ ...current, page: 1 })); }}><option value="">All KPIs</option>{metrics.map((metric) => <option key={metric.metric_key} value={metric.metric_key}>{metric.display_name}</option>)}</select></label>
          <label className="kpi-config-field"><span>Status</span><select value={filter.valueStatus} onChange={(event) => { setFilter({ ...filter, valueStatus: event.target.value }); setHistory((current) => ({ ...current, page: 1 })); }}><option value="">All statuses</option><option value="recorded">Recorded</option><option value="not_recorded">Not recorded</option><option value="not_applicable">Not applicable</option></select></label>
        </div>
        <div className="kpi-daily-table-wrap"><table className="kpi-daily-table"><thead><tr><th>Date</th><th>Employee</th><th>Team</th><th>Designation</th><th>KPI</th><th>Value</th><th>Status</th><th>Source</th><th>Entered by</th><th>Last updated</th><th>Actions</th></tr></thead><tbody>
          {history.rows.map((entry) => <tr key={entry.id}><td>{entry.business_date}</td><td>{entry.employee_name || entry.user_id}</td><td>{entry.team_snapshot || "—"}</td><td>{entry.designation_snapshot}</td><td>{metrics.find((metric) => metric.metric_key === entry.metric_key)?.display_name || entry.metric_key}</td><td>{formatValue(entry.value)}</td><td>{statusLabel(entry.value_status)}</td><td>{entry.source_type}{entry.source_name ? ` · ${entry.source_name}` : ""}</td><td>{entry.entered_by_name || "—"}</td><td>{entry.updated_at}</td><td className="kpi-daily-actions"><button type="button" title="View audit history" aria-label={`View audit history for ${entry.employee_name} ${entry.metric_key}`} onClick={() => openAudit(entry)}><FileClock size={16} aria-hidden="true" /></button><button type="button" title="Correct KPI record" aria-label={`Correct ${entry.employee_name} ${entry.metric_key}`} onClick={() => beginCorrection(entry)}><ShieldCheck size={16} aria-hidden="true" /></button></td></tr>)}
          {!history.rows.length && !loading && <tr><td colSpan="11" className="kpi-daily-no-results">No KPI values match these filters.</td></tr>}
        </tbody></table></div>
        <div className="kpi-daily-pagination"><span>{history.total} record{history.total === 1 ? "" : "s"} · Page {history.totalPages ? history.page : 0} of {history.totalPages}</span><div><button type="button" disabled={history.page <= 1} onClick={() => setHistory((current) => ({ ...current, page: current.page - 1 }))}>Previous</button><button type="button" disabled={history.page >= history.totalPages} onClick={() => setHistory((current) => ({ ...current, page: current.page + 1 }))}>Next</button></div></div>
      </section>}

      {selectedAudit && <div className="kpi-daily-overlay" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setSelectedAudit(null); }}><section className="kpi-daily-audit" role="dialog" aria-modal="true" aria-labelledby="kpi-daily-audit-title"><header><div><span>Daily KPI record</span><h2 id="kpi-daily-audit-title">Audit history</h2></div><button type="button" onClick={() => setSelectedAudit(null)} aria-label="Close audit">×</button></header>
        {auditLoading ? <p>Loading audit history…</p> : <><p>{selectedAudit.entry?.business_date} · {selectedAudit.entry?.employee_name} · {selectedAudit.entry?.metric_key}</p><ol>{(selectedAudit.events || []).map((event) => <li key={event.id}><time>{event.changed_at}</time><strong>{event.action === "created" ? "Value recorded" : "Value corrected"}</strong><span>Changed by {event.changed_by_name || event.changed_by}</span>{event.action === "corrected" && <p>Old: {statusLabel(event.old_value_status)} · {formatValue(event.old_value)}</p>}<p>New: {statusLabel(event.new_value_status)} · {formatValue(event.new_value)}</p>{event.reason && <p>Reason: {event.reason}</p>}</li>)}</ol></>}
      </section></div>}
    </main>
  );
}
